import type {
  AppointmentBookingDraftV1,
  AppointmentDraftMode,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  validateAppointmentBookingDraft,
  validateAppointmentStepOneBookingDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import type { LocationAppointmentEligibilityPublicationV1 } from "@/app/booking/adapters/locationAppointmentCatalog";
import { appendSpecialistGroupBookingResumeHandoff } from "@/app/booking/adapters/specialistGroupBookingResume";
import {
  BOOKING_FLOW_ORIGIN_PARAM,
  BOOKING_FLOW_ORIGIN_LOCATION,
  BOOKING_FLOW_ORIGIN_SPECIALIST,
  getBookingFlowSelectedServiceIds,
  getBookingFlowSelectedServiceVariantSelections,
  serializeBookingFlowServiceVariantSelections,
} from "@/app/components/booking-flow/bookingFlowCatalog";
import { isSpecialistStepOneFeasible } from "@/app/booking/core/specialistStepOneFeasibility";
import {
  locationServicesRouteDataFixtureSource,
  resolveLocationServicesRouteData,
  type LocationServicesRouteDataSource,
} from "@/app/locations/locationServicesRouteData";
import {
  getSpecialistAssignmentByRoute,
  getSpecialistOfferingsForAssignment,
  getSpecialistServicesForAssignment,
  getSpecialistWorkplace,
} from "@/app/specialists/catalog";
import type {
  SpecialistAssignment,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import { isSpecialistServiceConfigurationAvailableAtWorkplace } from "@/app/specialists/serviceConfigurationAvailability";
import { buildSpecialistServicesHref } from "@/app/specialists/routes";
import {
  SPECIALIST_SERVICE_ADD_ONS_PARAM,
  getSpecialistServiceAddOnQuerySelections,
  resolveSpecialistServiceAddOnQuerySelections,
  resolveSpecialistServiceConfiguration,
  serializeSpecialistServiceAddOnQuerySelections,
  type SpecialistServiceAddOnQuerySelection,
} from "@/app/specialists/serviceAddOns";
import {
  SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
  createSpecialistServiceBundleAddOnPlan,
  getSpecialistServiceBundleAddOnQuerySelections,
  resolveSpecialistServiceBundleConfiguration,
  serializeSpecialistServiceBundleAddOnQuerySelections,
  type SpecialistServiceBundleAddOnQuerySelection,
} from "@/app/specialists/serviceBundleAddOns";
import {
  specialistOfferingRequiresVariantSelection,
} from "@/app/specialists/serviceVariants";
import { buildLocationServicesHref } from "@/app/locations/routes";
import {
  buildAppointmentSchedulePlan,
  createAppointmentSchedulePlanStaffIndependentLineage,
} from "@/app/booking/scheduling/appointmentSchedulePlan";
import {
  appointmentTimeProfessionalMutationEvidenceMatchesDraft,
  type AppointmentTimeProfessionalMutationEvidenceV1,
  type AppointmentTimeProfessionalMutationProviderIdentity,
} from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";
import { appendAppointmentTimeServicesResumeHandoff } from "@/app/booking/scheduling/appointmentTimeServicesResumeHandoff";
import {
  specialistServicesRouteCapabilityMatchesDraft,
  type SpecialistServicesRouteCapabilityV1,
} from "@/app/booking/adapters/specialistServicesRouteCapability";

const MAX_CANONICAL_IDENTIFIER_LENGTH = 160;

export interface SpecialistAppointmentReturnSearchParamsLike {
  get(name: string): string | null;
  getAll(name: string): string[];
}

export interface SpecialistAppointmentReturnCatalogSource {
  getAssignmentByRoute: (
    locationId: string,
    userLocationId: string,
  ) => SpecialistAssignment | undefined;
  getWorkplace: (locationId: string) => SpecialistWorkplace | undefined;
  getServicesForAssignment: (
    userLocationId: string,
  ) => readonly SpecialistService[];
  getOfferingsForAssignment: (
    userLocationId: string,
  ) => readonly SpecialistServiceOffering[];
}

const specialistAppointmentReturnFixtureCatalog: SpecialistAppointmentReturnCatalogSource =
  {
    getAssignmentByRoute: getSpecialistAssignmentByRoute,
    getWorkplace: getSpecialistWorkplace,
    getServicesForAssignment: getSpecialistServicesForAssignment,
    getOfferingsForAssignment: getSpecialistOfferingsForAssignment,
  };

function getSingleCanonicalIdentifier(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
  name: string,
) {
  const values = searchParams.getAll(name);
  const value = values[0] ?? "";

  return values.length === 1 &&
    value.length > 0 &&
    value.length <= MAX_CANONICAL_IDENTIFIER_LENGTH &&
    value === value.trim()
    ? value
    : null;
}

function hasExactSpecialistOrigin(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
) {
  const values = searchParams.getAll(BOOKING_FLOW_ORIGIN_PARAM);

  return (
    values.length === 1 &&
    values[0] === BOOKING_FLOW_ORIGIN_SPECIALIST
  );
}

function hasExactLocationOrigin(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
) {
  const values = searchParams.getAll(BOOKING_FLOW_ORIGIN_PARAM);
  return values.length === 1 && values[0] === BOOKING_FLOW_ORIGIN_LOCATION;
}

function hasCompleteAssignmentCatalog(input: {
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
}) {
  const { assignment, workplace, services, offerings } = input;
  const assignmentServiceIds = new Set(assignment.serviceIds);
  const serviceIds = services.map(({ serviceId }) => serviceId);
  const offeringServiceIds = offerings.map(({ serviceId }) => serviceId);
  const offeringIds = offerings.map(({ offeringId }) => offeringId);

  return (
    workplace.locationId === assignment.locationId &&
    assignmentServiceIds.size === assignment.serviceIds.length &&
    services.length === assignment.serviceIds.length &&
    offerings.length === assignment.serviceIds.length &&
    new Set(serviceIds).size === serviceIds.length &&
    new Set(offeringServiceIds).size === offeringServiceIds.length &&
    new Set(offeringIds).size === offeringIds.length &&
    serviceIds.every((serviceId) => assignmentServiceIds.has(serviceId)) &&
    offerings.every(
      (offering) =>
        assignmentServiceIds.has(offering.serviceId) &&
        offering.userLocationId === assignment.userLocationId &&
        offering.offeringId.length > 0 &&
        offering.offeringId === offering.offeringId.trim(),
    )
  );
}

function getRequestedAppointmentMode(): AppointmentDraftMode {
  // Specialist Group remains unavailable until exact scheduling capability
  // proves that parallel participants can share the fixed entry specialist.
  return "individual";
}

function getSelectedNestedQueryRowCounts(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
  paramName:
    | typeof SPECIALIST_SERVICE_ADD_ONS_PARAM
    | typeof SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
  selectedServiceIds: readonly string[],
) {
  const selectedServiceIdSet = new Set(selectedServiceIds);
  const rowCountByParentId = new Map<string, number>();

  searchParams.getAll(paramName).forEach((rawValue) => {
    rawValue.split(",").forEach((rawRow) => {
      const rawParentId = rawRow.split(":", 1)[0] ?? "";
      let parentId: string;

      try {
        parentId = decodeURIComponent(rawParentId).trim();
      } catch {
        return;
      }

      if (!selectedServiceIdSet.has(parentId)) {
        return;
      }

      rowCountByParentId.set(
        parentId,
        (rowCountByParentId.get(parentId) ?? 0) + 1,
      );
    });
  });

  return rowCountByParentId;
}

function didServiceAddOnQueryResolveAtomically(
  querySelections: readonly SpecialistServiceAddOnQuerySelection[],
  resolvedSelections: readonly {
    groupId: string;
    addOnId: string;
    quantity: number;
  }[],
) {
  if (querySelections.length !== resolvedSelections.length) {
    return false;
  }

  const unmatchedSelections = [...resolvedSelections];

  return querySelections.every((querySelection) => {
    const matchingIndex = unmatchedSelections.findIndex(
      (resolvedSelection) =>
        (querySelection.groupId === null ||
          querySelection.groupId === resolvedSelection.groupId) &&
        querySelection.addOnId === resolvedSelection.addOnId &&
        querySelection.quantity === resolvedSelection.quantity,
    );

    if (matchingIndex < 0) {
      return false;
    }

    unmatchedSelections.splice(matchingIndex, 1);
    return true;
  });
}

function getAppointmentServicesReturnHrefOptions(input: {
  mode: AppointmentDraftMode;
  selections: readonly SpecialistServiceSelection[];
}) {
  const { mode, selections } = input;
  const serviceIds = selections.map(({ serviceId }) => serviceId);
  const serviceVariants = serializeBookingFlowServiceVariantSelections(
    selections.flatMap((selection) =>
      selection.variantId
        ? [
            {
              serviceId: selection.serviceId,
              variantId: selection.variantId,
            },
          ]
        : [],
    ),
    serviceIds,
  );
  const addOnRows: SpecialistServiceAddOnQuerySelection[] =
    selections.flatMap((selection) =>
      (selection.addOns ?? []).map(({ groupId, addOnId, quantity }) => ({
        serviceId: selection.serviceId,
        groupId,
        addOnId,
        quantity,
      })),
    );
  const bundleAddOnRows: SpecialistServiceBundleAddOnQuerySelection[] =
    selections.flatMap((selection) =>
      (selection.bundleItems ?? []).flatMap((item) =>
        item.addOns.map(({ groupId, addOnId, quantity }) => ({
          parentServiceId: selection.serviceId,
          bundleItemId: item.bundleItemId,
          serviceId: item.serviceId,
          groupId,
          addOnId,
          quantity,
        })),
      ),
    );
  const serviceAddOns = serializeSpecialistServiceAddOnQuerySelections(
    addOnRows,
    serviceIds,
  );
  const serviceBundleAddOns =
    serializeSpecialistServiceBundleAddOnQuerySelections(
      bundleAddOnRows,
      serviceIds,
    );

  if (
    (addOnRows.length > 0 && !serviceAddOns) ||
    (bundleAddOnRows.length > 0 && !serviceBundleAddOns)
  ) {
    return null;
  }

  return {
    appointmentMode: mode,
    serviceIds,
    serviceVariants,
    serviceAddOns,
    serviceBundleAddOns,
  } as const;
}

function buildSpecialistServicesReturnHrefFromSelections(input: {
  locationId: string;
  userLocationId: string;
  mode: AppointmentDraftMode;
  selections: readonly SpecialistServiceSelection[];
}) {
  const options = getAppointmentServicesReturnHrefOptions(input);

  return options
    ? buildSpecialistServicesHref(
        {
          locationId: input.locationId,
          userLocationId: input.userLocationId,
        },
        options,
      )
    : null;
}

function getAppointmentReturnSelections(
  draft: AppointmentBookingDraftV1,
) {
  const organizer = draft.participants.find(
    ({ participantId }) => participantId === draft.organizerParticipantId,
  );

  if (!organizer) {
    return null;
  }

  return [...organizer.serviceLines]
    .sort((left, right) => left.selectionOrder - right.selectionOrder)
    .map<SpecialistServiceSelection>(({ selection }) =>
      selection.kind === "service"
        ? {
            serviceId: selection.serviceId,
            variantId: selection.variantId,
            ...(selection.addOns.length > 0
              ? {
                  addOns: selection.addOns.map((addOn) => ({ ...addOn })),
                }
              : {}),
          }
        : {
            serviceId: selection.serviceId,
            variantId: null,
            bundleItems: selection.bundleItems.map((item) => ({
              bundleItemId: item.bundleItemId,
              serviceId: item.serviceId,
              variantId: item.variantId,
              addOns: item.addOns.map((addOn) => ({ ...addOn })),
            })),
          },
    );
}

function appendGroupResumeIfNeeded(
  draft: AppointmentBookingDraftV1,
  href: string,
) {
  return draft.mode === "group" && draft.participants.length > 1
    ? appendSpecialistGroupBookingResumeHandoff(href, {
        draftId: draft.draftId,
        schemaVersion: draft.schemaVersion,
      })
    : href;
}

/**
 * Rebuilds the entry-point-owned canonical Step-1 URL from the validated
 * draft itself. Compatibility query parameters are deliberately not an
 * input, so Location and Specialist origins share one exact serializer.
 */
export function buildAppointmentReturnHref(
  draft: AppointmentBookingDraftV1,
) {
  const selections = getAppointmentReturnSelections(draft);

  if (!selections) {
    return null;
  }

  const options = getAppointmentServicesReturnHrefOptions({
    mode: draft.mode,
    selections,
  });

  if (!options) {
    return null;
  }

  const returnHref =
    draft.entryPoint.kind === "specialist-services"
      ? buildSpecialistServicesHref(
          {
            locationId: draft.entryPoint.locationId,
            userLocationId: draft.entryPoint.userLocationId,
          },
          options,
        )
      : buildLocationServicesHref(draft.entryPoint.locationId, {
          ...options,
          initialServiceProfessionals: draft.entryPoint.initialServiceProfessionals,
          initialProfessionalId:
            draft.entryPoint.initialProfessionalUserLocationId,
        });

  return appendGroupResumeIfNeeded(draft, returnHref);
}

export type AppointmentCurrentAssignmentReturnProjection =
  | {
      kind: "location-services";
      assignment:
        | "any-professional"
        | "multiple-professionals"
        | "specific-professional";
      userLocationId?: string;
      href: string;
      origin: { kind: "specialist"; userLocationId: string };
    }
  | {
      kind: "specialist-services";
      assignment: "specific-professional";
      userLocationId: string;
      href: string;
      origin: { kind: "specialist"; userLocationId: string };
    };

function planLineageWithoutStaff(draft: AppointmentBookingDraftV1) {
  const result = buildAppointmentSchedulePlan(draft);

  if (!result.ok) {
    return null;
  }

  return createAppointmentSchedulePlanStaffIndependentLineage(result.plan);
}

/**
 * Projects Back from Time using the current assignment without rewriting the
 * immutable Specialist origin that owns Step-2 skip semantics.
 *
 * All-Any and mixed task graphs return to Location Services with no visible
 * professional identity. One common exact professional returns to that
 * Specialist Services document only when a separate current route capability
 * proves the destination catalog. A mutated graph requires current, exact
 * Time capability evidence; stale publication/provider/task lineage fails
 * closed.
 */
export function buildAppointmentCurrentAssignmentReturnProjection(input: {
  sourceDraft: AppointmentBookingDraftV1;
  workingDraft: AppointmentBookingDraftV1;
  timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  currentLocationEligibilityPublication?: LocationAppointmentEligibilityPublicationV1 | null;
  expectedTimeProfessionalProviderIdentity?: AppointmentTimeProfessionalMutationProviderIdentity | null;
  currentSpecialistServicesRouteCapability?: SpecialistServicesRouteCapabilityV1 | null;
  evaluatedAtEpochMs?: number;
}): AppointmentCurrentAssignmentReturnProjection | null {
  const sourceValidation = validateAppointmentStepOneBookingDraft(
    input.sourceDraft,
  );
  const workingValidation = validateAppointmentBookingDraft(
    input.workingDraft,
  );

  if (
    !sourceValidation.valid ||
    !workingValidation.valid ||
    sourceValidation.draft.entryPoint.kind !== "specialist-services" ||
    workingValidation.draft.entryPoint.kind !== "specialist-services" ||
    sourceValidation.draft.draftId !== workingValidation.draft.draftId ||
    sourceValidation.draft.locationId !== workingValidation.draft.locationId ||
    JSON.stringify(planLineageWithoutStaff(sourceValidation.draft)) !==
      JSON.stringify(planLineageWithoutStaff(workingValidation.draft))
  ) {
    return null;
  }

  const sourceDraft = sourceValidation.draft;
  const workingDraft = workingValidation.draft;
  const sourceEntryPoint = sourceDraft.entryPoint;

  if (sourceEntryPoint.kind !== "specialist-services") {
    return null;
  }

  const mutated = JSON.stringify(sourceDraft) !== JSON.stringify(workingDraft);
  const evidence = input.timeProfessionalMutationEvidence ?? null;
  const currentLocationEligibilityPublication =
    input.currentLocationEligibilityPublication ?? null;
  const expectedTimeProfessionalProviderIdentity =
    input.expectedTimeProfessionalProviderIdentity ?? null;
  if (
    mutated &&
    (!evidence ||
      !currentLocationEligibilityPublication ||
      !expectedTimeProfessionalProviderIdentity ||
      !appointmentTimeProfessionalMutationEvidenceMatchesDraft({
        draft: workingDraft,
        evidence,
        locationEligibilityPublication:
          currentLocationEligibilityPublication,
        expectedProviderIdentity:
          expectedTimeProfessionalProviderIdentity,
        evaluatedAtEpochMs: input.evaluatedAtEpochMs,
      }))
  ) {
    return null;
  }

  const planResult = buildAppointmentSchedulePlan(workingDraft);
  const selections = getAppointmentReturnSelections(workingDraft);

  if (!planResult.ok || !selections) {
    return null;
  }

  const options = getAppointmentServicesReturnHrefOptions({
    mode: workingDraft.mode,
    selections,
  });

  if (!options) {
    return null;
  }

  const tasks = planResult.plan.tasks;
  const allAny = tasks.every(
    ({ staffPreference }) => staffPreference.kind === "any",
  );
  const specificIds = new Set(
    tasks.flatMap(({ staffPreference }) =>
      staffPreference.kind === "specific"
        ? [staffPreference.userLocationId]
        : [],
    ),
  );
  const allOneSpecific =
    specificIds.size === 1 &&
    tasks.every(
      ({ staffPreference }) => staffPreference.kind === "specific",
    );
  const origin = {
    kind: "specialist" as const,
    userLocationId: sourceEntryPoint.userLocationId,
  };

  if (allOneSpecific) {
    const userLocationId = [...specificIds][0]!;
    const routeIsActionable =
      specialistServicesRouteCapabilityMatchesDraft({
        capability: input.currentSpecialistServicesRouteCapability,
        draft: workingDraft,
        currentLocationEligibilityPublication,
        userLocationId,
      });

    if (routeIsActionable) {
      const specialistHref = buildSpecialistServicesHref(
        { locationId: workingDraft.locationId, userLocationId },
        options,
      );
      const href = mutated
        ? appendAppointmentTimeServicesResumeHandoff(
            specialistHref,
            workingDraft.draftId,
          )
        : specialistHref;

      if (!href) {
        return null;
      }

      return {
        kind: "specialist-services",
        assignment: "specific-professional",
        userLocationId,
        href,
        origin,
      };
    }
  }

  const locationHref = buildLocationServicesHref(
    workingDraft.locationId,
    options,
  );

  const resumeHref = mutated
    ? appendAppointmentTimeServicesResumeHandoff(
        locationHref,
        workingDraft.draftId,
      )
    : locationHref;

  if (!resumeHref) {
    return null;
  }

  return {
    kind: "location-services",
    assignment: allAny
      ? "any-professional"
      : allOneSpecific
        ? "specific-professional"
        : "multiple-professionals",
    ...(allOneSpecific
      ? { userLocationId: [...specificIds][0]! }
      : {}),
    href: resumeHref,
    origin,
  };
}

/** Compatibility export for Specialist-only consumers during migration. */
export function buildSpecialistAppointmentReturnHref(
  draft: AppointmentBookingDraftV1,
) {
  return draft.entryPoint.kind === "specialist-services"
    ? buildAppointmentReturnHref(draft)
    : null;
}

/**
 * Fail-closed compatibility fallback used only when the canonical session
 * draft cannot be hydrated. Identity is accepted only from one exact,
 * canonical Specialist-origin tuple. Step-1 selections are rebuilt through
 * the current assignment catalog and domain configuration resolvers; invalid
 * parent rows drop independently and can never degrade into a parent-only
 * selection.
 */
export function buildSpecialistAppointmentFallbackReturnHref(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
  catalog: SpecialistAppointmentReturnCatalogSource =
    specialistAppointmentReturnFixtureCatalog,
) {
  if (!hasExactSpecialistOrigin(searchParams)) {
    return null;
  }

  const locationId = getSingleCanonicalIdentifier(
    searchParams,
    "location",
  );
  const userLocationId = getSingleCanonicalIdentifier(
    searchParams,
    "staff",
  );

  if (!locationId || !userLocationId) {
    return null;
  }

  const assignment = catalog.getAssignmentByRoute(
    locationId,
    userLocationId,
  );

  if (
    !assignment ||
    !assignment.isPublicProfileVisible ||
    assignment.locationId !== locationId ||
    assignment.userLocationId !== userLocationId
  ) {
    return null;
  }

  const workplace = catalog.getWorkplace(locationId);
  const services = catalog.getServicesForAssignment(userLocationId);
  const offerings = catalog.getOfferingsForAssignment(userLocationId);

  if (
    !workplace ||
    !hasCompleteAssignmentCatalog({
      assignment,
      workplace,
      services,
      offerings,
    })
  ) {
    return null;
  }

  const serviceById = new Map(
    services.map((service) => [service.serviceId, service]),
  );
  const offeringByServiceId = new Map(
    offerings.map((offering) => [offering.serviceId, offering]),
  );
  const bundlePlanByServiceId = new Map(
    services.flatMap((service) => {
      const parentOffering = offeringByServiceId.get(service.serviceId);

      if (service.kind !== "bundle" || !parentOffering) {
        return [];
      }

      const plan = createSpecialistServiceBundleAddOnPlan({
        parentService: service,
        parentOffering,
        resolveChild: (serviceId) => {
          const childService = serviceById.get(serviceId);
          const childOffering = offeringByServiceId.get(serviceId);

          return childService && childOffering
            ? { service: childService, offering: childOffering }
            : null;
        },
      });

      return plan ? ([[service.serviceId, plan]] as const) : [];
    }),
  );
  const requestedServiceIds = getBookingFlowSelectedServiceIds(
    searchParams,
  ).filter((serviceId) => {
    const service = serviceById.get(serviceId);
    const offering = offeringByServiceId.get(serviceId);

    return Boolean(
      service &&
        offering &&
        isSpecialistStepOneFeasible({
          branch: "service",
          owner: {
            locationId: assignment.locationId,
            userLocationId: assignment.userLocationId,
            catalogVersion: workplace.catalogVersion,
          },
          service,
        }) &&
        isSpecialistServiceConfigurationAvailableAtWorkplace({
          assignment,
          workplace,
          service,
          offering,
          bundlePlan: bundlePlanByServiceId.get(serviceId),
        }),
    );
  });
  const requestedVariantSelections =
    getBookingFlowSelectedServiceVariantSelections(searchParams);
  const variantIdByServiceId = new Map(
    requestedVariantSelections.map(({ serviceId, variantId }) => [
      serviceId,
      variantId,
    ]),
  );
  const requestedAddOnSelections =
    getSpecialistServiceAddOnQuerySelections(
      searchParams,
      requestedServiceIds,
    );
  const requestedBundleAddOnSelections =
    getSpecialistServiceBundleAddOnQuerySelections(
      searchParams,
      requestedServiceIds,
      ({ parentServiceId, bundleItemId, serviceId, addOnId }) => {
        const plan = bundlePlanByServiceId.get(parentServiceId);
        const step = plan?.steps.find(
          (candidate) =>
            candidate.bundleItem.itemId === bundleItemId &&
            candidate.service.serviceId === serviceId,
        );
        const matchingGroups =
          step?.addOnGroups.filter((group) =>
            group.options.some((option) => option.addOnId === addOnId),
          ) ?? [];

        return matchingGroups.length === 1
          ? matchingGroups[0]!.groupId
          : null;
      },
    );
  const nestedQueryRowCounts = {
    serviceAddOns: getSelectedNestedQueryRowCounts(
      searchParams,
      SPECIALIST_SERVICE_ADD_ONS_PARAM,
      requestedServiceIds,
    ),
    serviceBundleAddOns: getSelectedNestedQueryRowCounts(
      searchParams,
      SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
      requestedServiceIds,
    ),
  };
  const selections = requestedServiceIds.flatMap<SpecialistServiceSelection>(
    (serviceId) => {
      const service = serviceById.get(serviceId);
      const offering = offeringByServiceId.get(serviceId);

      if (!service || !offering) {
        return [];
      }

      if (service.kind === "service") {
        const queryAddOns = requestedAddOnSelections.filter(
          (selection) => selection.serviceId === serviceId,
        );

        if (
          (nestedQueryRowCounts.serviceAddOns.get(serviceId) ?? 0) !==
            queryAddOns.length ||
          (nestedQueryRowCounts.serviceBundleAddOns.get(serviceId) ?? 0) > 0
        ) {
          return [];
        }

        const variantId = specialistOfferingRequiresVariantSelection(offering)
          ? (variantIdByServiceId.get(serviceId) ?? null)
          : null;
        const addOns = resolveSpecialistServiceAddOnQuerySelections(
          offering,
          queryAddOns,
        );

        if (!didServiceAddOnQueryResolveAtomically(queryAddOns, addOns)) {
          return [];
        }

        const resolved = resolveSpecialistServiceConfiguration(offering, {
          serviceId,
          variantId,
          ...(addOns.length > 0 ? { addOns } : {}),
        });

        return resolved ? [resolved.selection] : [];
      }

      const plan = bundlePlanByServiceId.get(serviceId);

      if (!plan) {
        return [];
      }

      const queryBundleAddOns = requestedBundleAddOnSelections.filter(
        (selection) => selection.parentServiceId === serviceId,
      );

      if (
        (nestedQueryRowCounts.serviceBundleAddOns.get(serviceId) ?? 0) !==
          queryBundleAddOns.length ||
        (nestedQueryRowCounts.serviceAddOns.get(serviceId) ?? 0) > 0
      ) {
        return [];
      }

      let mappedBundleAddOnCount = 0;
      const bundleItems = plan.steps.map((step) => {
        const addOns = queryBundleAddOns
          .filter(
            (selection) =>
              selection.bundleItemId === step.bundleItem.itemId &&
              selection.serviceId === step.service.serviceId,
          )
          .map(({ groupId, addOnId, quantity }) => ({
            groupId,
            addOnId,
            quantity,
          }));

        mappedBundleAddOnCount += addOns.length;

        return {
          bundleItemId: step.bundleItem.itemId,
          serviceId: step.service.serviceId,
          variantId: step.variantId,
          addOns,
        };
      });

      if (mappedBundleAddOnCount !== queryBundleAddOns.length) {
        return [];
      }

      const resolved = resolveSpecialistServiceBundleConfiguration(plan, {
        serviceId,
        variantId: null,
        bundleItems,
      });

      return resolved?.canConfirm ? [resolved.selection] : [];
    },
  );

  return buildSpecialistServicesReturnHrefFromSelections({
    locationId,
    userLocationId,
    mode: getRequestedAppointmentMode(),
    selections,
  });
}

/**
 * Missing/expired Location handoffs recover only to the exact current
 * Location selector after the same complete route resolver confirms that the
 * current Services document exists. Compatibility query rows are intentionally
 * discarded: without the canonical draft they cannot prove a complete
 * configuration.
 */
export function buildLocationAppointmentFallbackReturnHref(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
  routeDataSource: LocationServicesRouteDataSource =
    locationServicesRouteDataFixtureSource,
) {
  if (!hasExactLocationOrigin(searchParams)) return null;

  const locationId = getSingleCanonicalIdentifier(searchParams, "location");

  if (!locationId) return null;

  try {
    return resolveLocationServicesRouteData(locationId, routeDataSource)
      ? buildLocationServicesHref(locationId)
      : null;
  } catch {
    return null;
  }
}

export function buildAppointmentFallbackReturnHref(
  searchParams: SpecialistAppointmentReturnSearchParamsLike,
) {
  return (
    buildSpecialistAppointmentFallbackReturnHref(searchParams) ??
    buildLocationAppointmentFallbackReturnHref(searchParams)
  );
}
